import memoizeOne from 'memoize-one';
import { Country } from '~/Api/Country';

import ActualPack from './ActualPack';
import UnitPrice from '~/Api/Product/UnitPrice';

export default class VirtualPack {
	id: string;

	distinctName: string;

	dosageStrength: string;

	dosageForm: string;

	packageSize: string;

	packageContainer: string | null;

	real: boolean;

	actualPacks: ActualPack[];

	constructor(
		id: string,
		distinctName: string,
		dosageStrength: string,
		dosageForm: string,
		packageSize: string,
		packageContainer: string | null,
		real: boolean,
		actualPacks: ActualPack[]
	) {
		this.id = id;
		this.distinctName = distinctName;
		this.dosageStrength = dosageStrength;
		this.dosageForm = dosageForm;
		this.packageSize = packageSize;
		this.packageContainer = packageContainer;
		this.real = real;
		this.actualPacks = actualPacks;
	}

	get pricedActualPacks(): (ActualPack & { hasPrice: true })[] {
		return this.filterPricedActualPacks(this.actualPacks);
	}

	filterPricedActualPacks = memoizeOne((actualPacks: ActualPack[]) =>
		actualPacks.filter(
			(actualPack): actualPack is ActualPack & { hasPrice: true } =>
				!actualPack.status.isRemoved && actualPack.hasPrices
		)
	);

	get validActualPacks(): ActualPack[] {
		return this.filterValidActualPacks(this.actualPacks);
	}

	filterValidActualPacks = memoizeOne((actualPacks: ActualPack[]) =>
		actualPacks.filter((actualPack) => !actualPack.status.isRemoved)
	);

	get other() {
		return this.distinctName.toLowerCase() === 'other';
	}

	calculateLowestPrices(): {
		actualPack: ActualPack;
		lowestPrice: ActualPack['lowestPrice'];
	}[] {
		type ByCountry = {
			[key in Country['alpha2']]?: { actualPack: ActualPack; lowestPrice: ActualPack['lowestPrice'] };
		};

		const byCountry: ByCountry = this.actualPacks.reduce((result: ByCountry, actualPack: ActualPack) => {
			const { lowestPrice } = actualPack;
			if (actualPack.status.isRemoved || lowestPrice === null || lowestPrice.price === null) {
				return result;
			}
			const countryCode = actualPack.dataSource.country.alpha2;
			if (!result[countryCode] || (result[countryCode]?.lowestPrice?.price?.value || 0) > lowestPrice.price.value) {
				result[countryCode] = { actualPack, lowestPrice };
			}
			return result;
		}, {});

		const lowestPrices = Object.values(byCountry);
		return lowestPrices.sort(
			(a, b) => Number(a.lowestPrice?.price?.value || 0) - Number(b.lowestPrice?.price?.value || 0)
		);
	}

	calculateLowestUnitPrices(): {
		actualPack: ActualPack;
		unitPrice: UnitPrice;
	}[] {
		type ByCountry = {
			[key in Country['alpha2']]?: { actualPack: ActualPack; unitPrice: UnitPrice };
		};

		const byCountry: ByCountry = this.actualPacks.reduce((result: ByCountry, actualPack: ActualPack) => {
			const { unitPrice } = actualPack;
			if (actualPack.status.isRemoved || unitPrice === null || unitPrice.value === null) {
				return result;
			}
			const countryCode = actualPack.dataSource.country.alpha2;
			if (!result[countryCode] || (result[countryCode]?.unitPrice?.value || 0) > unitPrice.value) {
				result[countryCode] = { actualPack, unitPrice };
			}
			return result;
		}, {});

		const lowestPrices = Object.values(byCountry);
		return lowestPrices
			.sort((a, b) => Number(a.unitPrice?.value || 0) - Number(b.unitPrice?.value || 0))
			.filter((a) => a.unitPrice?.value);
	}
}
